import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { RetryConfig } from '../http-interceptors/retry.tokens';

@Injectable({
  providedIn: 'root',
})
export class RetryService {
  /**
   * Calculates the delay in milliseconds for the next retry attempt.
   * @param error The HttpErrorResponse or mapped error from the failed request.
   * @param attempt The current attempt number (0-indexed).
   * @param config The retry configuration.
   * @returns The delay in milliseconds.
   */
  public getDelay(
    error: HttpErrorResponse | { headers?: any },
    attempt: number,
    config: RetryConfig
  ): number {
    let delayMs = config.baseMs;

    const retryAfterHeader = error.headers?.get('Retry-After');
    if (retryAfterHeader) {
      const retryAfterSec = Number(retryAfterHeader);
      if (!Number.isNaN(retryAfterSec)) {
        delayMs = retryAfterSec * 1000;
      } else {
        const retryAfterDate = Date.parse(retryAfterHeader);
        if (!Number.isNaN(retryAfterDate)) {
          const delayFromNow = retryAfterDate - Date.now();
          if (delayFromNow > 0) {
            delayMs = delayFromNow;
          } else {
            delayMs = config.baseMs;
          }
        }
      }
    } else {
      if (config.multipliers && config.multipliers.length > attempt) {
        delayMs = config.baseMs * config.multipliers[attempt];
      } else {
        const baseDelay = config.baseMs * Math.pow(2, attempt);
        const jitter = Math.floor(Math.random() * (baseDelay / 2));
        delayMs = baseDelay + jitter;
      }
    }

    if (config.maxDelayMs) {
      delayMs = Math.min(delayMs, config.maxDelayMs);
    }
    return delayMs;
  }

  /**
   * Determines if the request should be retried based on the error status.
   * @param error The error object (HttpErrorResponse or object with code/status).
   * @param config The retry configuration.
   * @returns True if the request should be retried, false otherwise.
   */
  public shouldRetry(
    error: { status?: number; code?: number | string } | HttpErrorResponse,
    config: RetryConfig
  ): boolean {
    if (!config.retryStatuses) {
      return false;
    }

    // Check 'status' property
    if (
      error.status !== undefined &&
      config.retryStatuses.includes(error.status)
    ) {
      return true;
    }

    // Check 'code' property safely
    if ('code' in error) {
      const code = (error as { code: number | string }).code;
      if (
        code !== undefined &&
        config.retryStatuses.some((status) => status === code)
      ) {
        return true;
      }
    }

    return false;
  }
}
